import {
  clearCustomerAgentIdentity,
  endCustomerAgentSession,
  identifyCustomerAgent,
  openCustomerAgent,
} from '@winnin/customer-agent'

import type { ICustomerAgentServerIdentity } from './customer-agent.types'
import { hasExpired } from './customer-agent.utils'

// Holds the identity the app injects. Deliberately not read from a store: the email has to be the
// one the token was minted for, or a valid token would end up paired with the wrong address.
class CustomerAgent {
  private identity: ICustomerAgentServerIdentity | null = null

  setIdentity(identity: ICustomerAgentServerIdentity | null) {
    this.identity = identity
  }

  identify() {
    const { identity } = this

    // Blanked rather than skipped: an identification from earlier in the session is still in the
    // widget's settings, and that is what the next `load()` would read.
    if (!identity) {
      clearCustomerAgentIdentity()
      return
    }

    if (hasExpired(identity.expiresAt)) {
      clearCustomerAgentIdentity()
      // Fire-and-forget: the imperative call sites that open support cannot await.
      identity.refresh()
      return
    }

    identifyCustomerAgent({ email: identity.email, identificationToken: identity.token })
  }

  // Identify first: it is what puts the identity on `hsConversationsSettings` that `load()` reads.
  open() {
    this.identify()
    openCustomerAgent()
  }

  // Drops the identity, not the conversation, so a reply that landed while the customer was away
  // is still there when they come back. The held token goes too, or the next person on this
  // machine would inherit this one's proof.
  endSession() {
    this.identity = null
    endCustomerAgentSession()
  }
}

export const customerAgent = new CustomerAgent()
